import { normalizeErrorString } from '@medplum/core'
import type { Bundle } from '@medplum/fhirtypes'
import {
  BatchMutationBuilder,
  TransactionMutationBuilder,
} from '@seen/core/bundle-builder'
import type { SeenMedplumClient } from '@seen/core/client'
import {
  type QueryClient,
  type UseMutationOptions,
  type UseMutationResult,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { toast } from 'sonner'
import { cacheKeys } from '#src/hooks/cacheKeys.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'

export type UseFhirBundleMutationArgs = (
  builder: TransactionMutationBuilder | BatchMutationBuilder,
  {
    queryClient,
    medplum,
  }: { queryClient: QueryClient; medplum: SeenMedplumClient }
) => void | Promise<void>

export interface UseFhirBundleMutationOptions {
  mutation?: Omit<
    UseMutationOptions<Bundle, unknown, UseFhirBundleMutationArgs>,
    'mutationFn'
  >
}

export function useFhirBundleMutation(
  type: 'batch' | 'transaction',
  options?: UseFhirBundleMutationOptions | null
): UseMutationResult<Bundle, unknown, UseFhirBundleMutationArgs> {
  const medplum = useMedplum()
  const queryClient = useQueryClient()
  const isReadOnly = useIsReadOnly()
  return useMutation({
    ...options?.mutation,
    onError: (error, variables, context) => {
      toast.error(normalizeErrorString(error))
      options?.mutation?.onError?.(error, variables, context)
    },
    onSuccess: (data, variables, context) => {
      data.entry?.forEach((e) => {
        const { resourceType, id } = e.resource ?? {}
        if (!resourceType || !id) {
          return
        }
        // PATCH entries return a `Binary` payload in the response — NOT the
        // patched target resource. Writing Binary into the target's cache key
        // poisons reads until invalidation. Skip setQueryData for these.
        if (resourceType === 'Binary') {
          return
        }

        cacheKeys.invalidateMutation(queryClient, resourceType, id)
        queryClient.setQueryData(cacheKeys.read(resourceType, id), e.resource)
      })
      options?.mutation?.onSuccess?.(data, variables, context)
    },
    mutationFn: async (args) => {
      if (isReadOnly) {
        console.warn(
          '[read-only] mutation attempted: useFhirBundleMutation',
          type
        )
      }
      const builder =
        type === 'transaction'
          ? new TransactionMutationBuilder()
          : new BatchMutationBuilder()
      await args(builder, { queryClient, medplum })

      const result = await medplum.executeBatch(builder.bundle)

      // Invalidate patched resources after success. onSuccess skips Binary
      // entries, so without this the cache for patched resources stays stale.
      for (const context of builder.getPatchContexts().values()) {
        cacheKeys.invalidateMutation(
          queryClient,
          context.resourceType,
          context.id
        )
      }

      return result
    },
  })
}

export function useTransactionMutation(
  options?: UseFhirBundleMutationOptions | null
): UseMutationResult<Bundle, unknown, UseFhirBundleMutationArgs> {
  return useFhirBundleMutation('transaction', options)
}

export function useBatchMutation(
  options?: UseFhirBundleMutationOptions | null
): UseMutationResult<Bundle, unknown, UseFhirBundleMutationArgs> {
  return useFhirBundleMutation('batch', options)
}
